import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getUserTeams, createTeam as apiCreateTeam, joinTeamByCode, leaveTeam as apiLeaveTeam } from '../shared/storage/supabaseSync';

const TeamContext = createContext(null);

const CURRENT_TEAM_KEY = 'ccnp:currentTeamId';

/**
 * Team provider — manages team memberships and current team selection.
 * In local mode (no Supabase), provides empty state and team UI is hidden.
 */
export function TeamProvider({ children }) {
  const { user, isLocalMode, isAuthenticated } = useAuth();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load teams on auth state change
  useEffect(() => {
    if (isLocalMode || !isAuthenticated || !user) {
      setTeams([]);
      setCurrentTeam(null);
      setLoading(false);
      return;
    }
    loadTeams();
  }, [isLocalMode, isAuthenticated, user?.id]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const userTeams = await getUserTeams(user.id);
      setTeams(userTeams);

      // Restore last-selected team from localStorage
      const savedTeamId = localStorage.getItem(CURRENT_TEAM_KEY);
      const savedTeam = userTeams.find(t => t.id === savedTeamId);

      if (savedTeam) {
        setCurrentTeam(savedTeam);
      } else if (userTeams.length > 0) {
        // Fall back to first team
        setCurrentTeam(userTeams[0]);
        localStorage.setItem(CURRENT_TEAM_KEY, userTeams[0].id);
      } else {
        setCurrentTeam(null);
        localStorage.removeItem(CURRENT_TEAM_KEY);
      }
    } catch (err) {
      console.warn('Failed to load teams:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchTeam = useCallback((teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (team) {
      setCurrentTeam(team);
      localStorage.setItem(CURRENT_TEAM_KEY, teamId);
    }
  }, [teams]);

  const createTeam = useCallback(async (name) => {
    if (!user) return { error: { message: 'Not authenticated' } };

    const { data, error } = await apiCreateTeam(name, user.id);
    if (error) return { error };

    // Reload teams and select the new one
    const userTeams = await getUserTeams(user.id);
    setTeams(userTeams);
    const newTeam = userTeams.find(t => t.id === data.id);
    if (newTeam) {
      setCurrentTeam(newTeam);
      localStorage.setItem(CURRENT_TEAM_KEY, newTeam.id);
    }
    return { data, error: null };
  }, [user]);

  const joinTeam = useCallback(async (inviteCode) => {
    const { data, error } = await joinTeamByCode(inviteCode);
    if (error) return { error };

    // Reload teams and select the joined one
    const userTeams = await getUserTeams(user.id);
    setTeams(userTeams);
    const joinedTeam = userTeams.find(t => t.id === data.team_id);
    if (joinedTeam) {
      setCurrentTeam(joinedTeam);
      localStorage.setItem(CURRENT_TEAM_KEY, joinedTeam.id);
    }
    return { data, error: null };
  }, [user]);

  const leaveTeam = useCallback(async (teamId) => {
    if (!user) return { error: { message: 'Not authenticated' } };

    const { error } = await apiLeaveTeam(teamId, user.id);
    if (error) return { error };

    // Reload teams
    const userTeams = await getUserTeams(user.id);
    setTeams(userTeams);

    // If we left the current team, switch to another
    if (currentTeam?.id === teamId) {
      if (userTeams.length > 0) {
        setCurrentTeam(userTeams[0]);
        localStorage.setItem(CURRENT_TEAM_KEY, userTeams[0].id);
      } else {
        setCurrentTeam(null);
        localStorage.removeItem(CURRENT_TEAM_KEY);
      }
    }
    return { error: null };
  }, [user, currentTeam]);

  const refreshTeams = useCallback(() => {
    if (user) loadTeams();
  }, [user]);

  const value = {
    teams,
    currentTeam,
    loading,
    isTeamAdmin: currentTeam?.role === 'admin',
    hasTeams: teams.length > 0,
    switchTeam,
    createTeam,
    joinTeam,
    leaveTeam,
    refreshTeams,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}

export default TeamContext;
